/**
 * Income Verification report UI — V2 (portable).
 *
 * Figma redesign: category filters, declared-vs-actual comparison, stacked
 * deposit chart, month-by-month list, Flinks institutions, collapsible
 * disclaimers. Download passes the active category filter so the PDF matches
 * the modal snapshot. Kept separate from the frozen V1 modal for easy
 * production port via `verification-ui-version.ts`.
 */
import { useState, useSyncExternalStore } from 'react'
import {
	Bar,
	BarChart,
	Legend,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from 'recharts'
import type { ParsedStepData } from '#app/utils/application-detail-v2/types'
import { INCOME_DISCLAIMERS } from '#app/utils/application-detail-v2/verification-disclaimers'
import {
	type AiDeclaredIncomePeriod,
	INCOME_CATEGORY_COLORS,
	INCOME_CATEGORY_ORDER,
	INCOME_FILTER_LABELS,
	type IncomeCategoryKey,
	averageMonthlyDeposits,
	buildStackedMonthlySeries,
	buildDeclaredEmploymentPeriod,
	declaredMonthlyIncome,
	declaredPreviousMonthlyIncome,
	declaredReferenceMonthly,
	declaredSalaryCaption,
	defaultIncomeCategorySelection,
	filterIncomeItems,
	formatWholeAmount,
	groupTransactionsByMonth,
	incomeAverageMonthCount,
	incomeChartEndMonth,
	incomeChartMonthCap,
	isSelfEmployedEmploymentType,
	orderedIncomeCategories,
	parseAiDeclaredIncome,
	parseConnectedFlinksAccounts,
	parseVerificationSection,
	pickAccountHolder,
	presentIncomeCategories,
	yearlyFromMonthly,
} from '#app/utils/application-detail-v2/verification-display'
import {
	CollapsibleDisclaimers,
	FinancialInstitutionsList,
	IncomeCategoryFilters,
	IncomeComparisonSummary,
	IncomeModalShell,
	MonthByMonthList,
} from './verification-bits-v2'

type ChartRow = {
	month: string
	monthLabel: string
	total: number
} & Record<IncomeCategoryKey, number>

const SEGMENT_GAP = 3
const SEGMENT_RADIUS = 6

function roundedRectPath(
	x: number,
	y: number,
	w: number,
	h: number,
	r: number,
): string {
	const radius = Math.min(r, w / 2, h / 2)
	if (radius <= 0) {
		return `M${x},${y}H${x + w}V${y + h}H${x}Z`
	}
	return [
		`M${x + radius},${y}`,
		`H${x + w - radius}`,
		`Q${x + w},${y} ${x + w},${y + radius}`,
		`V${y + h - radius}`,
		`Q${x + w},${y + h} ${x + w - radius},${y + h}`,
		`H${x + radius}`,
		`Q${x},${y + h} ${x},${y + h - radius}`,
		`V${y + radius}`,
		`Q${x},${y} ${x + radius},${y}`,
		'Z',
	].join(' ')
}

/** Fully-rounded stack segment with a small vertical gap (Figma bar pills). */
function RoundedSegmentBar(props: {
	x?: number
	y?: number
	width?: number
	height?: number
	fill?: string
}) {
	const { x = 0, y = 0, width = 0, height = 0, fill } = props
	if (height <= 0 || width <= 0) return null
	const gap = Math.min(SEGMENT_GAP, Math.max(0, height - 2))
	const drawHeight = Math.max(height - gap, 1)
	const drawY = y + gap / 2
	return (
		<path
			d={roundedRectPath(x, drawY, width, drawHeight, SEGMENT_RADIUS)}
			fill={fill}
		/>
	)
}

/** Tailwind `sm` (640px) — denser chart axis/label treatment below. */
function useIsBelowSm(): boolean {
	return useSyncExternalStore(
		(onChange) => {
			const mql = window.matchMedia('(max-width: 639px)')
			mql.addEventListener('change', onChange)
			return () => mql.removeEventListener('change', onChange)
		},
		() => window.matchMedia('(max-width: 639px)').matches,
		() => false,
	)
}

/** On <sm, keep ~6 ticks (always include first + last) so labels don't collide. */
function shouldShowMonthTick(
	index: number,
	monthCount: number,
	compact: boolean,
): boolean {
	if (!compact || monthCount <= 6) return true
	if (index === 0 || index === monthCount - 1) return true
	const step = Math.ceil(monthCount / 6)
	return index % step === 0
}

function MonthAxisTick({
	x = 0,
	y = 0,
	payload,
	index = 0,
	compact = false,
	monthCount = 0,
}: {
	x?: number
	y?: number
	payload?: { value?: string }
	index?: number
	compact?: boolean
	monthCount?: number
}) {
	if (!shouldShowMonthTick(index, monthCount, compact)) return null
	const raw = String(payload?.value ?? '')
	const [month = raw, year = ''] = raw.split(/\s+/)
	return (
		<g transform={`translate(${x},${y})`}>
			<text
				dy={12}
				textAnchor="middle"
				fill="#9CA3AF"
				fontSize={compact ? 10 : 11}
				fontWeight={500}
			>
				{month}
			</text>
			{!compact && year ? (
				<text dy={26} textAnchor="middle" fill="#9CA3AF" fontSize={10}>
					{year}
				</text>
			) : null}
		</g>
	)
}

/** Right gutter so the ReferenceLine label never clips at the SVG edge. */
function declaredLabelRightMargin(label: string, compact: boolean): number {
	// Compact (<sm): 11px ≈ 6.2px/char; desktop: 14px ≈ 8.4px/char.
	const pxPerChar = compact ? 6.2 : 8.4
	const pad = compact ? 14 : 20
	return Math.ceil(label.length * pxPerChar) + pad
}

function DeclaredReferenceLabel({
	viewBox,
	value,
	compact = false,
}: {
	viewBox?: { x?: number; y?: number; width?: number }
	value?: string | number
	compact?: boolean
}) {
	if (!viewBox || value == null) return null
	const x = (viewBox.x ?? 0) + (viewBox.width ?? 0) + (compact ? 4 : 8)
	const y = viewBox.y ?? 0
	return (
		<text
			x={x}
			y={y}
			dy={compact ? 4 : 5}
			fill="#32302F"
			fontSize={compact ? 11 : 14}
			textAnchor="start"
		>
			{String(value)}
		</text>
	)
}

function IncomeChartTooltip({
	active,
	payload,
}: {
	active?: boolean
	payload?: Array<{
		dataKey?: string | number
		value?: number | string
		payload?: ChartRow
	}>
}) {
	if (!active || !payload?.length) return null
	const row = payload[0]?.payload
	if (!row) return null
	const entries = INCOME_CATEGORY_ORDER.filter((key) =>
		payload.some((entry) => entry.dataKey === key),
	)
	return (
		<div className="min-w-[220px] rounded-[20px] bg-white px-4 py-3.5 shadow-[0_12px_40px_rgba(50,48,47,0.12)]">
			<div className="flex items-center justify-between gap-6">
				<span className="text-[16px] font-semibold text-[#32302F]">
					{row.monthLabel}
				</span>
				<span className="text-[16px] font-semibold tabular-nums text-[#32302F]">
					{formatWholeAmount(row.total)}
				</span>
			</div>
			<ul className="mt-1 flex flex-col gap-2">
				{entries.map((key) => (
					<li
						key={key}
						className="flex items-center justify-between gap-6 text-[14px]"
					>
						<span className="flex items-center gap-2 text-[#32302F]">
							<span
								className="size-2.5 shrink-0 rounded-full"
								style={{ backgroundColor: INCOME_CATEGORY_COLORS[key] }}
							/>
							{INCOME_FILTER_LABELS[key]}
						</span>
						<span className="font-medium tabular-nums text-[#32302F]">
							{formatWholeAmount(row[key] ?? 0)}
						</span>
					</li>
				))}
			</ul>
		</div>
	)
}

function periodFromStep(
	netMonthly: number | null,
): AiDeclaredIncomePeriod | null {
	if (netMonthly == null) return null
	return {
		netMonthly,
		grossMonthly: yearlyFromMonthly(netMonthly),
	}
}

export function IncomeVerificationModalV2({
	open,
	onOpenChange,
	aiReportData,
	employmentStep,
	verifyIncomeStep,
	showRequestBank,
	onRequestBank,
	canDownload,
	onDownload,
	downloadBusy,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	aiReportData: Record<string, unknown> | null
	employmentStep: ParsedStepData['employment']
	verifyIncomeStep: ParsedStepData['verify-income']
	showRequestBank: boolean
	onRequestBank: () => void
	canDownload: boolean
	onDownload: (categories?: IncomeCategoryKey[]) => void
	downloadBusy: boolean
}) {
	const section = parseVerificationSection(aiReportData, 'income')
	const items = section?.items ?? []
	const present = presentIncomeCategories(items)
	const selfEmployed = isSelfEmployedEmploymentType(
		employmentStep?.employmentType,
	)
	const initialSelected = defaultIncomeCategorySelection(present, {
		selfEmployed,
	})

	// Reset selection whenever the dialog opens (props-driven state adjust).
	const [prevOpen, setPrevOpen] = useState(open)
	const [selected, setSelected] = useState<IncomeCategoryKey[]>(initialSelected)
	if (open !== prevOpen) {
		setPrevOpen(open)
		if (open) {
			setSelected(defaultIncomeCategorySelection(present, { selfEmployed }))
		}
	}

	const filtered = filterIncomeItems(items, selected)
	const holder = pickAccountHolder(items)
	const aiDeclared = parseAiDeclaredIncome(aiReportData)
	const stepMonthly = declaredMonthlyIncome(employmentStep)
	const previousStepMonthly = declaredPreviousMonthlyIncome(employmentStep)

	const current: AiDeclaredIncomePeriod | null = aiDeclared
		? aiDeclared.current
		: periodFromStep(stepMonthly)
	const previous: AiDeclaredIncomePeriod | null = aiDeclared
		? aiDeclared.previous
		: periodFromStep(previousStepMonthly)
	const showPrevious = Boolean(
		aiDeclared ? aiDeclared.previous : previous != null,
	)

	const currentTimeline = buildDeclaredEmploymentPeriod(employmentStep, 'current')
	const previousTimeline = buildDeclaredEmploymentPeriod(employmentStep, 'previous')

	const salaryCaption = declaredSalaryCaption(aiDeclared, employmentStep)
	const averageMonthly = averageMonthlyDeposits(filtered, {
		monthCount: incomeAverageMonthCount(aiReportData),
	})
	const referenceMonthly = declaredReferenceMonthly(aiDeclared, employmentStep)
	const declaredLabel =
		referenceMonthly != null
			? `Declared ${formatWholeAmount(referenceMonthly)}`
			: null
	const series = buildStackedMonthlySeries(filtered, {
		spanItems: items,
		maxMonths: incomeChartMonthCap(aiReportData),
		endMonth: incomeChartEndMonth(aiReportData),
	})
	// Chart reads newest→oldest left-to-right (latest month first).
	const chartRows: ChartRow[] = series.map((row) => ({
		month: row.shortLabel,
		monthLabel: row.label,
		...row.byCategory,
		total: row.total,
	}))
	const monthGroups = groupTransactionsByMonth(filtered)
	const institutions = parseConnectedFlinksAccounts(verifyIncomeStep)
	// Selected keys in fixed stack order (for legend / tooltip).
	const activeLegend = orderedIncomeCategories(selected).filter((key) =>
		present.includes(key),
	)
	const isBelowSm = useIsBelowSm()
	// Always mount Bars for every present category in fixed order. Toggling
	// via `hide` (not conditional mount) — Recharts appends remounted stack
	// segments to the end, which shuffled order on re-select.

	return (
		<IncomeModalShell
			open={open}
			onOpenChange={onOpenChange}
			title="Income Verification"
			accountHolder={holder}
			showRequestBank={showRequestBank}
			onRequestBank={onRequestBank}
			canDownload={canDownload}
			onDownload={() => onDownload(orderedIncomeCategories(selected))}
			downloadBusy={downloadBusy}
			filters={
				<IncomeCategoryFilters
					present={present}
					selected={selected}
					onChange={setSelected}
				/>
			}
		>
			<IncomeComparisonSummary
				current={current}
				previous={previous}
				currentTimeline={currentTimeline}
				previousTimeline={previousTimeline}
				showPrevious={showPrevious}
				salaryCaption={salaryCaption}
				averageMonthly={averageMonthly}
			/>

			{chartRows.length > 0 ? (
				<section className="mt-5 rounded-2xl border border-[#EFEFED] bg-white p-5">
					<h3 className="text-[15px] font-medium text-[#32302F] 2xl:text-[20px]">
						Actual Deposits By Month Vs. Declared
					</h3>
					<div className="mt-4 h-[300px] w-full overflow-visible">
						<ResponsiveContainer width="100%" height="100%">
							<BarChart
								data={chartRows}
								margin={{
									top: 12,
									right:
										declaredLabel != null
											? declaredLabelRightMargin(declaredLabel, isBelowSm)
											: isBelowSm
												? 12
												: 24,
									left: 0,
									bottom: 8,
								}}
								barCategoryGap="18%"
							>
								<XAxis
									dataKey="month"
									tick={
										<MonthAxisTick
											compact={isBelowSm}
											monthCount={chartRows.length}
										/>
									}
									tickLine={false}
									axisLine={false}
									height={isBelowSm ? 24 : 36}
									interval={0}
								/>
								{/* Include declared reference in maxY so the line isn't
								    clipped when it sits above every month's deposits
								    (same rule as IncomeDepositsChart PDF twin). */}
								<YAxis
									hide
									domain={[
										0,
										(dataMax: number) =>
											Math.max(dataMax, referenceMonthly ?? 0),
									]}
								/>
								<Tooltip
									cursor={false}
									content={<IncomeChartTooltip />}
									wrapperStyle={{ outline: 'none' }}
								/>
								{declaredLabel != null && referenceMonthly != null ? (
									<ReferenceLine
										y={referenceMonthly}
										stroke="#747474"
										strokeDasharray="4 4"
										label={
											<DeclaredReferenceLabel
												value={declaredLabel}
												compact={isBelowSm}
											/>
										}
									/>
								) : null}
								{present.map((key) => (
									<Bar
										key={key}
										dataKey={key}
										stackId="income"
										hide={!selected.includes(key)}
										legendType="none"
										fill={INCOME_CATEGORY_COLORS[key]}
										shape={RoundedSegmentBar}
										maxBarSize={44}
									/>
								))}
								<Legend
									verticalAlign="bottom"
									align="left"
									content={() => (
										<ul className="m-0 flex list-none flex-wrap gap-x-4 gap-y-2 p-0 pt-2">
											{activeLegend.map((key) => (
												<li
													key={key}
													className="flex items-center gap-1.5 text-[12px] text-[#32302F]"
												>
													<span
														className="size-2 shrink-0 rounded-full"
														style={{
															backgroundColor: INCOME_CATEGORY_COLORS[key],
														}}
														aria-hidden="true"
													/>
													{INCOME_FILTER_LABELS[key]}
												</li>
											))}
										</ul>
									)}
								/>
							</BarChart>
						</ResponsiveContainer>
					</div>
				</section>
			) : null}

			<div className="mt-5">
				<MonthByMonthList groups={monthGroups} />
			</div>

			<FinancialInstitutionsList accounts={institutions} />

			<CollapsibleDisclaimers disclaimers={INCOME_DISCLAIMERS} />
		</IncomeModalShell>
	)
}
